import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import AppLoader from 'components/general/AppLoader';
import { Alert, Button, Col, Form, Row } from "react-bootstrap"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import FullScreenModal from "components/general/defaultModals/FullScreenModal";
import StandardModal from "components/general/defaultModals/StandardModal";
import useSortableList from "components/general/useSortableList";
import { getLegacyInstanceRegistrationStatus } from "services/instanceRegistration";

const emptyTemplateItem = {
  item_title: '',
  agendaEvent: '',
  meetingRole: '',
}

export default function AgendaTemplates({ reloadToken = 0, directOpen = false, onClose, navigationParent = null }) {
  const { user: authUser } = useSelector((x) => x.auth)
  const url = process.env.REACT_APP_URL
  const isOfficer = authUser?.user?.acl?.toLowerCase() === 'officer'

  const [templates, setTemplates] = useState([])
  const [meetingRoles, setMeetingRoles] = useState([])
  const [agendaEvents, setAgendaEvents] = useState([])
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [templateItems, setTemplateItems] = useState([{ ...emptyTemplateItem }])
  const [contestPluginEnabled, setContestPluginEnabled] = useState(false)
  const [isContest, setIsContest] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [showEditor, setShowEditor] = useState(false)
  const [templateSearch, setTemplateSearch] = useState('')
  const itemEventRefs = useRef({})
  const focusItemIndexRef = useRef(null)
  const selectedTemplateIdRef = useRef('')
  const lastReloadTokenRef = useRef(0)

  const authHeaders = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${authUser?.token || ''}`,
  }), [authUser?.token])

  const sortedAgendaEvents = useMemo(
    () => [...agendaEvents].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''))),
    [agendaEvents]
  )

  const sortedMeetingRoles = useMemo(
    () => [...meetingRoles].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''))),
    [meetingRoles]
  )

  const applyTemplateToForm = useCallback((template) => {
    if (!template) {
      setTemplateName('')
      setTemplateItems([{ ...emptyTemplateItem }])
      setIsContest(false)
      return
    }

    setTemplateName(template.name || '')
    setIsContest(Boolean(template.isContest))

    const sorted = Array.isArray(template.templateItems)
      ? [...template.templateItems].sort((a, b) => (a.position || 0) - (b.position || 0))
      : []

    const normalized = sorted.map((item) => ({
      item_title: item.item_title || '',
      agendaEvent: item.agendaEvent?._id || item.agendaEvent || '',
      meetingRole: item.meetingRole?._id || item.meetingRole || '',
    }))

    setTemplateItems(normalized.length > 0 ? normalized : [{ ...emptyTemplateItem }])
  }, [])

  const selectTemplate = useCallback((templateId, templateList = templates) => {
    setSelectedTemplateId(templateId)
    if (!templateId) {
      setTemplateName('')
      setTemplateItems([{ ...emptyTemplateItem }])
      return
    }

    const template = templateList.find(t => t._id === templateId)
    if (!template) return

    applyTemplateToForm(template)
  }, [applyTemplateToForm, templates])

  const loadData = useCallback(async (preserveSelectedTemplateId = false) => {
    if (!authUser?.token || !isOfficer) return
    setLoading(true)

    try {
      const [templatesRes, rolesRes, eventsRes] = await Promise.all([
        fetch(`${url}/agendatemplates`, { headers: authHeaders }),
        fetch(`${url}/meetingroles`, { headers: authHeaders }),
        fetch(`${url}/agendaEvents`, { headers: authHeaders }),
      ])

      const templatesData = templatesRes.ok ? await templatesRes.json() : []
      const rolesData = rolesRes.ok ? await rolesRes.json() : []
      const eventsData = eventsRes.ok ? await eventsRes.json() : []
      const nextTemplates = Array.isArray(templatesData) ? templatesData : []

      setTemplates(nextTemplates)
      setMeetingRoles(Array.isArray(rolesData) ? rolesData : [])
      setAgendaEvents(Array.isArray(eventsData) ? eventsData : [])

      const currentSelectedId = selectedTemplateIdRef.current
      if (preserveSelectedTemplateId && currentSelectedId) {
        const selectedTemplate = nextTemplates.find((template) => template?._id === currentSelectedId)
        if (selectedTemplate) {
          setSelectedTemplateId(selectedTemplate._id)
          applyTemplateToForm(selectedTemplate)
        }
      }
    } catch (e) {
      toast.error('Failed to load agenda templates configuration data.', { position: 'bottom-left' })
    } finally {
      setLoading(false)
    }
  }, [applyTemplateToForm, authHeaders, authUser?.token, isOfficer, url])

  useEffect(() => {
    if (directOpen) setShowTemplates(true)
  }, [directOpen])

  useEffect(() => {
    selectedTemplateIdRef.current = selectedTemplateId
  }, [selectedTemplateId])

  useEffect(() => {
    loadData()
  }, [loadData])

  useEffect(() => {
    if (!authUser?.token || !isOfficer) return
    let active = true
    async function loadContestPluginState() {
      try {
        const registration = await getLegacyInstanceRegistrationStatus()
        const controllerBaseUrl = String(registration?.controllerBaseUrl || '').replace(/\/+$/, '')
        const instanceId = String(registration?.instanceId || '').trim()
        if (!controllerBaseUrl || !instanceId) return
        const response = await fetch(`${controllerBaseUrl}/api/instances/${encodeURIComponent(instanceId)}/plugins`, {
          headers: { Authorization: `Bearer ${authUser.token}` }
        })
        if (!response.ok) return
        const data = await response.json()
        if (active) setContestPluginEnabled(data?.plugins?.contesthub === true)
      } catch {
        if (active) setContestPluginEnabled(false)
      }
    }
    loadContestPluginState()
    return () => { active = false }
  }, [authUser?.token, isOfficer])

  useEffect(() => {
    if (reloadToken > 0 && reloadToken !== lastReloadTokenRef.current) {
      lastReloadTokenRef.current = reloadToken
      loadData(true)
    }
  }, [loadData, reloadToken])

  const handleCreateTemplate = () => {
    setSelectedTemplateId('')
    setTemplateName('')
    setTemplateItems([{ ...emptyTemplateItem }])
    setIsContest(false)
    setShowEditor(true)
  }

  const handleEditTemplate = (template) => {
    selectTemplate(template._id)
    setShowEditor(true)
  }

  const handleDuplicateTemplate = (template) => {
    const sorted = Array.isArray(template.templateItems)
      ? [...template.templateItems].sort((a, b) => (a.position || 0) - (b.position || 0))
      : []
    setSelectedTemplateId('')
    setTemplateName(`${template.name || 'Template'} Copy`)
    setIsContest(Boolean(template.isContest))
    setTemplateItems(sorted.map((item) => ({
      item_title: item.item_title || '',
      agendaEvent: item.agendaEvent?._id || item.agendaEvent || '',
      meetingRole: item.meetingRole?._id || item.meetingRole || '',
    })))
    setShowEditor(true)
  }

  const handleDeleteTemplate = async (template) => {
    await handleDelete(template._id)
  }

  const updateTemplateItem = (index, field, value) => {
    setTemplateItems(prev => prev.map((item, idx) => idx === index ? { ...item, [field]: value } : item))
  }

  const addTemplateItem = () => {
    setTemplateItems(prev => {
      focusItemIndexRef.current = prev.length
      return [...prev, { ...emptyTemplateItem }]
    })
  }

  useEffect(() => {
    if (focusItemIndexRef.current === null) return
    const index = focusItemIndexRef.current
    const frame = window.requestAnimationFrame(() => {
      itemEventRefs.current[index]?.focus()
      focusItemIndexRef.current = null
    })
    return () => window.cancelAnimationFrame(frame)
  }, [templateItems.length])

  const removeTemplateItem = (index) => {
    setTemplateItems(prev => {
      if (prev.length === 1) return [{ ...emptyTemplateItem }]
      return prev.filter((_, idx) => idx !== index)
    })
  }

  const moveTemplateItem = (from, to) => {
    setTemplateItems(prev => {
      const next = [...prev]
      const [draggedItem] = next.splice(from, 1)
      next.splice(to, 0, draggedItem)
      return next
    })
  }
  const templateSort = useSortableList({ onMove: moveTemplateItem })

  const handleSave = async () => {
    if (!isOfficer) {
      toast.error('Only officers can manage agenda templates.', { position: 'bottom-left' })
      return
    }

    if (!templateName.trim()) {
      toast.error('Template name is required.', { position: 'bottom-left' })
      return
    }

    const invalidItem = templateItems.find(item => !item.agendaEvent || !item.meetingRole)
    if (invalidItem) {
      toast.error('Each template item requires both Event and Role.', { position: 'bottom-left' })
      return
    }

    const payload = {
      name: templateName.trim(),
      templateItems: templateItems.map((item, idx) => ({
        position: idx + 1,
        item_title: item.item_title || '',
        agendaEvent: item.agendaEvent,
        meetingRole: item.meetingRole,
      })),
      isContest: contestPluginEnabled && isContest,
    }

    setSaving(true)
    try {
      const endpoint = selectedTemplateId
        ? `${url}/agendatemplate/${selectedTemplateId}`
        : `${url}/agendatemplate`
      const method = selectedTemplateId ? 'PATCH' : 'PUT'

      const res = await fetch(endpoint, {
        method,
        headers: authHeaders,
        body: JSON.stringify(payload),
      })

      const data = await res.json()
      if (!res.ok) {
        throw new Error(data?.Error || data?.error || 'Failed to save template.')
      }

      toast.success(selectedTemplateId ? 'Agenda template updated.' : 'Agenda template created.', { position: 'bottom-left' })

      const savedTemplate = data && typeof data === 'object' ? data : null
      if (savedTemplate?._id) {
        const nextTemplates = Array.isArray(templates)
          ? [...templates.filter((template) => template?._id !== savedTemplate._id), savedTemplate].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
          : [savedTemplate]

        setTemplates(nextTemplates)
        setSelectedTemplateId(savedTemplate._id)
        selectTemplate(savedTemplate._id, nextTemplates)
      } else {
        await loadData()
      }
    } catch (e) {
      toast.error(e.message, { position: 'bottom-left' })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (templateId = selectedTemplateId) => {
    if (!isOfficer) {
      toast.error('Only officers can manage agenda templates.', { position: 'bottom-left' })
      return
    }

    if (!templateId) return
    if (!window.confirm('Delete this agenda template?')) return

    setSaving(true)
    try {
      const res = await fetch(`${url}/agendatemplate/${templateId}`, {
        method: 'DELETE',
        headers: authHeaders,
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data?.Error || data?.error || 'Failed to delete template.')
      }

      toast.success('Agenda template deleted.', { position: 'bottom-left' })
      setSelectedTemplateId('')
      setTemplateName('')
      setTemplateItems([{ ...emptyTemplateItem }])
      await loadData()
    } catch (e) {
      toast.error(e.message, { position: 'bottom-left' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <AppLoader />
  }

  if (!isOfficer) {
    return <Alert variant="warning" className="mb-0">Only officers can manage agenda templates.</Alert>
  }

  const filteredTemplates = templates.filter((template) =>
    String(template?.name || '').toLowerCase().includes(templateSearch.trim().toLowerCase())
  )

  const editorBody = (
    <Form>
      <Row className="mb-3">
        <Col>
          <Form.Label>Template Name</Form.Label>
          <Form.Control value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="e.g. Regular Meeting" />
        </Col>
      </Row>
      {contestPluginEnabled ? (
        <Form.Check
          className="mb-3"
          type="checkbox"
          label="Contest agenda"
          checked={isContest}
          onChange={(event) => setIsContest(event.target.checked)}
        />
      ) : null}

      <Row className="mb-2"><Col><strong>Template Items (ordered)</strong></Col></Row>
      <div {...templateSort.containerProps}>
      {templateItems.map((item, index) => (
        <Row className="mb-2" key={`template-item-${index}`} {...templateSort.itemProps(index)}>
          <Col xs={12} md={1} className="d-flex align-items-center justify-content-center"><span {...templateSort.handleProps(index)} title="Drag to reorder" aria-label={`Drag item ${index + 1} to reorder`} style={{ cursor: 'grab', userSelect: 'none', fontWeight: 700 }}>#{index + 1}</span></Col>
          <Col xs={12} md={3}><Form.Control value={item.item_title} onChange={(e) => updateTemplateItem(index, 'item_title', e.target.value)} placeholder="Item title (optional)" /></Col>
          <Col xs={12} md={3}><Form.Select ref={(element) => { itemEventRefs.current[index] = element }} value={item.agendaEvent} onChange={(e) => updateTemplateItem(index, 'agendaEvent', e.target.value)}><option value="">Select event</option>{sortedAgendaEvents.map(event => <option key={event._id} value={event._id}>{event.name}</option>)}</Form.Select></Col>
          <Col xs={12} md={3}><Form.Select value={item.meetingRole} onChange={(e) => updateTemplateItem(index, 'meetingRole', e.target.value)}><option value="">Select role</option>{sortedMeetingRoles.map(role => <option key={role._id} value={role._id}>{role.name}</option>)}</Form.Select></Col>
          <Col xs={12} md={2} className="d-flex gap-1 justify-content-end"><Button variant="outline-danger" onClick={() => removeTemplateItem(index)} title="Remove"><i className="bi bi-trash" /></Button></Col>
        </Row>
      ))}
      </div>
      <Row className="mb-3"><Col><Button variant="outline-primary" onClick={addTemplateItem}><i className="bi bi-plus-circle" /> Add Item</Button></Col></Row>
      <Row><Col className="d-flex gap-2 justify-content-end"><Button variant="primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Template'}</Button></Col></Row>
    </Form>
  )
  const TemplatesModal = directOpen ? StandardModal : FullScreenModal

  return (
    <>
      {!directOpen && <Button variant="secondary" className="w-100" onClick={() => setShowTemplates(true)}><i className="bi bi-list-ul" /> Agenda Templates</Button>}
      <TemplatesModal navigationParent={navigationParent} show={showTemplates} handleClose={() => { setShowTemplates(false); onClose?.() }} title="Agenda Templates" style={{ maxWidth: '95%', marginTop: '4vh' }} ModalBodyComponent={(
        <div className="container-fluid py-3 agenda-templates-manager">
          <Row className="mb-3"><Col><Form.Control type="search" placeholder="Search templates" value={templateSearch} onChange={(event) => setTemplateSearch(event.target.value)} /></Col><Col xs="auto"><Button variant="primary" onClick={handleCreateTemplate}><i className="bi bi-plus-circle" /> Create Template</Button></Col></Row>
          {filteredTemplates.map((template) => <div className="d-flex align-items-center justify-content-between border-bottom py-3" key={template._id}><div><strong>{template.name}</strong><div className="text-muted">{template.templateItems?.length || 0} items{contestPluginEnabled && template.isContest ? ' · Contest' : ''}</div></div><div className="d-flex gap-2"><Button variant="outline-primary" onClick={() => handleEditTemplate(template)}>Edit</Button><Button variant="outline-secondary" onClick={() => handleDuplicateTemplate(template)}>Duplicate</Button><Button variant="outline-danger" onClick={() => handleDeleteTemplate(template)}>Delete</Button></div></div>)}
          {filteredTemplates.length === 0 ? <Alert variant="light">No templates found.</Alert> : null}
        </div>
      )} />
      <StandardModal navigationParent={navigationParent ? `modal-${navigationParent}` : null} show={showEditor} handleClose={() => setShowEditor(false)} title={selectedTemplateId ? 'Edit Agenda Template' : 'Create Agenda Template'} ModalBodyComponent={editorBody} />
    </>
  )
}